import {
  moneyProcessingPubSub,
  MONEY_PROCESSING_EVENT_ID,
  type DMPocketMoneyEventData,
  useWorkingTimeStore
} from '@nsftx/seven-client-retail-vue-dm'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'
import { useMoneyProcessingStore } from '@/stores/money-processing/moneyProcessingStore'
import { formatNumber, useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { getWallets } from '@/modules/wallet/walletService'
import errorParser from '@/utils/errorParser'
import { useWalletStore } from '@/stores/wallet/walletStore'
import i18n from '@/plugins/translationsPlugin'

const LOG_PREFIX = '[moneyProcessingService]'
const MONEY_PROCESSING_FAILED_NOTIFICATION_ID = 'money_processing_failed_notification'

let subscriptionToken: string | null = null

const { t } = i18n.global

const getEventCurrency = (data: DMPocketMoneyEventData): string => {
  if ('currency' in data && data.currency) return data.currency

  const { activeWallet } = useWalletStore()
  return activeWallet?.currency ?? ''
}

const showMoneyProcessingFailedNotification = (amount: string, currency: string) => {
  const formattedAmount = formatNumber(amount, { style: 'decimal' })
  useNotificationsStore().showNotification({
    id: MONEY_PROCESSING_FAILED_NOTIFICATION_ID,
    variant: 'error',
    text: [t('money_processing_failed_notification', { amount: formattedAmount, currency })]
  })
}

const handleMoneyProcessingEvent = (_topic: string, data?: DMPocketMoneyEventData) => {
  if (!data?.type) return

  const { isValid, nextValidTime } = useWorkingTimeStore().isWorkingTimeValid()
  if (!isValid) {
    logService.error(`${LOG_PREFIX} Money could not be processed. Working time is over.`, {
      code: 'ST_POCKET_MONEY_PROCESSING_WORKING_TIME_ERROR',
      details: {
        type: data.type
      }
    })
    useNotificationsStore().showNotification({
      variant: 'warning',
      text: [t('working_time_over', { time: nextValidTime || t('common_unknown') })]
    })

    return
  }

  const moneyProcessingStore = useMoneyProcessingStore()
  const showMoneyIndicator = useCmsConfigStore().cmsConfig?.appSettings.showMoneyIndicator ?? false

  getWallets('ui').catch((err) => {
    logService.error(`${LOG_PREFIX} Failed to get wallets for the punter on pocket money event.`, {
      code: 'ST_GET_WALLETS_FAILED',
      ...errorParser.parseUpstream(err)
    })
  })

  logService.debug(`${LOG_PREFIX} Pocket money event received`, {
    code: 'ST_POCKET_MONEY_EVENT_RECEIVED',
    details: {
      ...data
    }
  })

  switch (data.type) {
    case 'moneyProcessing':
      if (showMoneyIndicator) {
        moneyProcessingStore.showMoneyTopUpIndicator(data.amount, getEventCurrency(data))
      }
      break
    case 'moneyProcessed':
      moneyProcessingStore.hideMoneyTopUpIndicator()
      break
    case 'moneyProcessingFailed':
    case 'moneyUnprocessed':
      moneyProcessingStore.hideMoneyTopUpIndicator()
      showMoneyProcessingFailedNotification(data.amount, getEventCurrency(data))
      break
  }
}

const initMoneyProcessingService = () => {
  subscriptionToken = moneyProcessingPubSub.subscribe(
    MONEY_PROCESSING_EVENT_ID,
    handleMoneyProcessingEvent
  )
}

const _resetForTest = () => {
  if (subscriptionToken) {
    moneyProcessingPubSub.unsubscribe(subscriptionToken)
    subscriptionToken = null
  }
}

export default {
  initMoneyProcessingService,
  MONEY_PROCESSING_FAILED_NOTIFICATION_ID,
  _resetForTest
}
